/**
 *
 * Header
 *
 */
import React, { useMemo } from 'react';
import { Button, Menu, Row, Select, Space } from 'antd';
import { CalendarOutlined, LayoutOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import LangSwitch from '../LangSwitch/LangSwitch';
import * as S from './Header.styles';
import { selectUser } from 'app/containers/Authorization/slice/selectors';
import {
  selectEnvs,
  selectSelectedEnv,
} from 'app/containers/HiikoEnv/slice/selectors';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import {
  selectAllSeasons,
  selectSelectedSeasonId,
} from 'app/containers/Season/slice/selectors';
import { useSeasonSlice } from 'app/containers/Season/slice';
import {
  HIIKO_ENV_SELECT_DISABLED_ROUTES,
  RouteConstants,
  SEASON_SELECT_DISABLED_ROUTES,
} from 'app/routes';
import { CompanySelect } from '../CompanySelect/CompanySelect';
import { logoutUser } from 'utils/helpers';
import ShowIfDesktop from 'utils/responsiveQuery/ShowIfDesktop';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';
import ToggleSidebarButton from '../Sidebar/ToggleSidebarButton';

const Header = () => {
  const { t } = useTranslation();
  const { mobileOrTablet } = useResponsiveQuery();

  const envs = useSelector(selectEnvs);
  const selectedEnv = useSelector(selectSelectedEnv);

  const seasons = useSelector(selectAllSeasons);
  const selectedSeasonId = useSelector(selectSelectedSeasonId);

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { actions: seasonActions } = useSeasonSlice();

  const user = useSelector(selectUser);

  const onLogout = () => {
    logoutUser();
  };

  const onEnvSelect = (clickedEnvId: number) => {
    if (clickedEnvId) {
      const splittedUrl = location.pathname.split('/');
      const envIndex = splittedUrl.findIndex(part => part === 'env');
      splittedUrl[envIndex + 1] = String(clickedEnvId);
      const updatedUrl = splittedUrl.join('/');
      history.push(updatedUrl);
    } else {
      history.push(RouteConstants.hiikoEnv.list);
    }
  };

  const onSeasonSelect = (clickedSeasonId: number) => {
    dispatch(seasonActions.setSelectedSeason(clickedSeasonId || null));
  };

  const { isHiikoEnvSelectVisible, isSeasonSelectVisible } = useMemo(() => {
    let isVisibleEnv = true;
    HIIKO_ENV_SELECT_DISABLED_ROUTES.forEach(route => {
      if (matchPath(location.pathname, route)) {
        isVisibleEnv = false;
      }
    });

    let isVisibleSeason = true;
    SEASON_SELECT_DISABLED_ROUTES.forEach(route => {
      if (matchPath(location.pathname, route)) {
        isVisibleSeason = false;
      }
    });

    return {
      isHiikoEnvSelectVisible: isVisibleEnv,
      isSeasonSelectVisible: isVisibleSeason,
    };
  }, [location.pathname]);

  return (
    <S.Header>
      <S.HeaderLayout justify="space-between" align="middle" wrap={false}>
        <Space size={12}>
          <ToggleSidebarButton />
          {isHiikoEnvSelectVisible && (
            <Row align="middle" wrap={false}>
              <LayoutOutlined className="var-select-icon" />
              <ShowIfDesktop>
                <S.SelectTitle>
                  {t('Header.Environment', 'Environment')}:
                </S.SelectTitle>
              </ShowIfDesktop>

              <Select
                bordered={false}
                value={selectedEnv?.id}
                dropdownMatchSelectWidth={false}
                onSelect={onEnvSelect}
                style={{ maxWidth: mobileOrTablet ? 100 : 'unset' }}
              >
                {envs.map(env => (
                  <Select.Option key={env.id} value={env.id}>
                    {env.name}
                  </Select.Option>
                ))}
                <Select.Option key={'0'} value={0}>
                  <Button type="link" style={{ paddingLeft: 0 }}>
                    {t('Header.AllEnvs', 'All environments')}
                  </Button>
                </Select.Option>
              </Select>
            </Row>
          )}

          <CompanySelect />

          {isSeasonSelectVisible && (
            <Row align="middle" wrap={false}>
              <CalendarOutlined className="var-select-icon" />
              <ShowIfDesktop>
                <S.SelectTitle>{t('Header.Season', 'Season')}:</S.SelectTitle>
              </ShowIfDesktop>

              <Select
                bordered={false}
                value={selectedSeasonId || 0}
                dropdownMatchSelectWidth={false}
                onSelect={onSeasonSelect}
              >
                {seasons?.map(season => (
                  <Select.Option key={season.id} value={season.id}>
                    {season.year}
                  </Select.Option>
                ))}
              </Select>
            </Row>
          )}
          {/* {process.env.NODE_ENV !== 'production' && (
              <>
                <Permissions />
                <Tag>{isConsultant ? 'Consultant' : 'Not consultant'}</Tag>
              </>
            )} */}
        </Space>

        <Row justify="end">
          <Space>
            <LangSwitch />
            <Menu mode="horizontal">
              <Menu.SubMenu
                style={{ margin: 0 }}
                key="SubMenu"
                title={
                  <ShowIfDesktop>{`${user?.name} ${user?.lastName}`}</ShowIfDesktop>
                }
                icon={
                  <S.AvatarImg style={{ backgroundColor: '#000' }}>
                    {user?.name?.charAt(0)}
                  </S.AvatarImg>
                }
              >
                <Menu.Item onClick={onLogout}>
                  {t('Header.Logout', 'Logout')}
                </Menu.Item>
              </Menu.SubMenu>
            </Menu>
          </Space>
        </Row>
      </S.HeaderLayout>
    </S.Header>
  );
};

export default Header;
